import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterRenderEffect,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import moment from 'moment';

export interface ScheduleRangeSliderValue {
  offset: number;
  duration: number;
}

type SliderHandle = 'start' | 'end' | 'wrap';

@Component({
  selector: 'rc-schedule-range-slider',
  templateUrl: './schedule-range-slider.component.html',
  styleUrls: ['./schedule-range-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
})
export class ScheduleRangeSliderComponent {
  offset = input<number>(0);
  duration = input<number>(0);
  periodLength = input<number>(1440);
  step = input<number>(15);
  startDate = input<string>();
  /** exclusive upper bound for the start position, defaults to the period length */
  maxOffset = input<number>();

  valueChange = output<ScheduleRangeSliderValue>();

  private track = viewChild.required<ElementRef<HTMLElement>>('track');

  private startBubble = viewChild<ElementRef<HTMLElement>>('startBubble');
  private endBubble = viewChild<ElementRef<HTMLElement>>('endBubble');
  private wrapBubble = viewChild<ElementRef<HTMLElement>>('wrapBubble');

  activeHandle = signal<SliderHandle | null>(null);

  private trackWidth = signal(0);
  private bubbleWidths = signal({ start: 0, end: 0, wrap: 0 });

  private period = computed(() => this.periodLength() || 1440);

  private effectiveStep = computed(() => this.step() || 15);

  private maxStart = computed(() => Math.min(this.maxOffset() ?? this.period(), this.period()));

  private value = linkedSignal<ScheduleRangeSliderValue>(() => ({
    offset: this.offset() || 0,
    duration: this.duration() || 0,
  }));

  currentOffset = computed(() => this.value().offset);
  endMinutes = computed(() => this.value().offset + this.value().duration);
  isWrapped = computed(() => this.endMinutes() > this.period());
  wrappedEnd = computed(() => this.endMinutes() - this.period());

  showsEndHandle = computed(() => !this.isWrapped());
  showsWrapHandle = computed(
    () => this.isWrapped() || (this.currentOffset() > 0 && this.endMinutes() === this.period()),
  );

  startPercent = computed(() => this.toPercent(this.currentOffset()));
  endPercent = computed(() => this.toPercent(this.endMinutes()));
  wrapPercent = computed(() => this.toPercent(this.isWrapped() ? this.wrappedEnd() : 0));

  activeSegments = computed(() => {
    const start = this.currentOffset();
    const end = this.endMinutes();
    const period = this.period();
    if (end > period) {
      return [
        { left: this.toPercent(start), width: this.toPercent(Math.max(0, period - start)) },
        { left: 0, width: this.toPercent(Math.max(0, end - period)) },
      ];
    }
    return [{ left: this.toPercent(start), width: this.toPercent(Math.max(0, end - start)) }];
  });

  startLabel = computed(() => this.getGeneralString(this.currentOffset()));
  endLabel = computed(() => this.getGeneralString(this.endMinutes()));
  wrapLabel = computed(() => {
    if (this.period() === 10080) {
      return this.getGeneralString(this.endMinutes());
    }
    return `${this.getDateString(this.wrappedEnd())} (+1d)`;
  });

  wrappedHandles = computed<Set<SliderHandle>>(() => {
    const width = this.trackWidth();
    if (!width) return new Set();
    const widths = this.bubbleWidths();
    const gap = 8;
    const toPx = (percent: number) => (percent / 100) * width;
    const start = { pos: toPx(this.startPercent()), width: widths.start };
    const end = { pos: toPx(this.endPercent()), width: widths.end };
    const wrap = { pos: toPx(this.wrapPercent()), width: widths.wrap };
    const collides = (a: { pos: number; width: number }, b: { pos: number; width: number }) =>
      Math.abs(a.pos - b.pos) < (a.width + b.width) / 2 + gap;
    const wrapped = new Set<SliderHandle>();
    if (this.showsEndHandle() && collides(start, end)) {
      wrapped.add(end.pos >= start.pos ? 'end' : 'start');
    }
    if (this.showsWrapHandle() && collides(start, wrap)) {
      wrapped.add(wrap.pos >= start.pos ? 'wrap' : 'start');
    }
    return wrapped;
  });

  constructor() {
    afterRenderEffect(() => {
      this.currentOffset();
      this.endMinutes();
      this.period();
      this.showsEndHandle();
      this.showsWrapHandle();

      const trackEl = this.track()?.nativeElement;
      if (!trackEl) return;
      const width = Math.round(trackEl.getBoundingClientRect().width);
      const widths = {
        start: this.startBubble()?.nativeElement.offsetWidth ?? 0,
        end: this.endBubble()?.nativeElement.offsetWidth ?? 0,
        wrap: this.wrapBubble()?.nativeElement.offsetWidth ?? 0,
      };

      if (width !== this.trackWidth()) {
        this.trackWidth.set(width);
      }
      const prev = this.bubbleWidths();
      if (widths.start !== prev.start || widths.end !== prev.end || widths.wrap !== prev.wrap) {
        this.bubbleWidths.set(widths);
      }
    });

    const resizeObserver = new ResizeObserver(() => {
      const trackEl = this.track()?.nativeElement;
      if (!trackEl) return;
      const width = Math.round(trackEl.getBoundingClientRect().width);
      if (width !== this.trackWidth()) {
        this.trackWidth.set(width);
      }
    });

    effect(() => {
      const trackEl = this.track()?.nativeElement;
      if (trackEl) resizeObserver.observe(trackEl);
    });

    inject(DestroyRef).onDestroy(() => resizeObserver.disconnect());
  }

  toPercent(minutes: number): number {
    return (minutes * 100) / this.period();
  }

  onPointerDown(event: PointerEvent, handle: SliderHandle) {
    if (event.button !== 0) return;
    event.preventDefault();
    this.activeHandle.set(handle);
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.applyPointerValue(event);
  }

  onPointerMove(event: PointerEvent) {
    if (!this.activeHandle()) return;
    this.applyPointerValue(event);
  }

  onPointerUp() {
    this.activeHandle.set(null);
  }

  onKeydown(event: KeyboardEvent, handle: SliderHandle) {
    const step = this.effectiveStep();
    let delta: number;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      delta = -step;
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      delta = step;
    } else {
      return;
    }
    event.preventDefault();
    this.applyHandleValue(handle, this.getHandleMinutes(handle) + delta);
  }

  private getHandleMinutes(handle: SliderHandle): number {
    switch (handle) {
      case 'start':
        return this.currentOffset();
      case 'end':
        return this.endMinutes();
      case 'wrap':
        return this.isWrapped() ? this.wrappedEnd() : 0;
    }
  }

  private applyPointerValue(event: PointerEvent) {
    const handle = this.activeHandle();
    if (!handle) return;
    const rect = this.track().nativeElement.getBoundingClientRect();
    const ratio = Math.min(Math.max((event.clientX - rect.left) / (rect.width || 1), 0), 1);
    this.applyHandleValue(handle, ratio * this.period());
  }

  private applyHandleValue(handle: SliderHandle, minutes: number) {
    const period = this.period();
    const step = this.effectiveStep();
    const start = this.currentOffset();
    const end = this.endMinutes();
    const snapped = Math.round(minutes / step) * step;
    let newValue: ScheduleRangeSliderValue;

    switch (handle) {
      case 'start': {
        const lower = this.isWrapped() ? this.wrappedEnd() : 0;
        const upper = Math.max(lower, Math.min(end - 1, this.maxStart() - 1));
        const newStart = Math.max(lower, Math.min(snapped, upper));
        newValue = { offset: newStart, duration: end - newStart };
        break;
      }
      case 'end': {
        const lower = start + 1;
        const upper = Math.max(lower, period);
        const newEnd = Math.max(lower, Math.min(snapped, upper));
        newValue = { offset: start, duration: newEnd - start };
        break;
      }
      case 'wrap': {
        const upper = Math.max(0, start);
        const newWrappedEnd = Math.max(0, Math.min(snapped, upper));
        newValue = { offset: start, duration: period - start + newWrappedEnd };
        break;
      }
    }

    const current = this.value();
    if (newValue.offset !== current.offset || newValue.duration !== current.duration) {
      this.value.set(newValue);
      this.valueChange.emit(newValue);
    }
  }

  getDateString(value: number): string {
    if (value === 0) return '00:00';
    const minutes = value % 60;
    const hours = (value - minutes) / 60;
    const hourString = hours < 10 ? `0${hours}` : hours;
    const minuteString = minutes < 10 ? `0${minutes}` : minutes;
    return `${hourString}:${minuteString}`;
  }

  private getGeneralString(fix: number): string {
    if (this.period() === 10080) {
      return moment(this.startDate())
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .add({ minute: fix })
        .format('ddd HH:mm');
    }
    return this.getDateString(fix);
  }
}
